import { useEffect, useRef, useState } from 'react';
import { spore } from '@/spore/runtime';
import { createTouchEngine } from '@/spore/activityEngine';
import { Slider } from '@/components/ui/slider';
import { Panel } from './Panel';

const CLASSES = ['TAP', 'STROKE', 'AMBIG', 'FLURRY', 'ALL_HANDS', 'STILL'];
const LABEL_KEYS = { t: 'TAP', k: 'STROKE', 0: 'HANDS_OFF', a: 'ALL_HANDS' };
const LABEL_OK = { TAP: ['TAP', 'AMBIG', 'FLURRY'], STROKE: ['STROKE', 'AMBIG', 'ALL_HANDS'], HANDS_OFF: ['STILL'], ALL_HANDS: ['ALL_HANDS', 'STROKE', 'FLURRY'] };

export const TouchPanel = () => {
  const t = window.__spore.touch;
  return (
    <Panel testId="touch-panel" code="00b / BASE STATE" title="Human Touch Detector" right={<button data-testid="relearn-baseline-button" className="btn-lab px-3 py-1 text-[0.65rem]" onClick={() => spore.relearnBaseline(10000)}>Re-learn base state (10s)</button>}>
      <div className="grid grid-cols-12 items-center gap-6">
        <div data-testid="touch-live-indicator" data-state={t.learning ? 'learning' : t.touchActive ? 'touch' : 'background'} className="col-span-12 border p-5 text-center lg:col-span-4" style={{ borderColor: t.touchActive ? 'var(--amber)' : 'var(--hair)', background: t.touchActive ? 'rgba(255,176,0,0.12)' : 'transparent' }}>
          <div className={`font-display text-3xl font-extrabold ${t.learning ? 'text-[var(--teal)]' : t.touchActive ? 'glow-amber' : 'text-[var(--muted)]'}`}>{t.learning ? `LEARNING ${(t.learnRemainingMs / 1000).toFixed(1)}s` : t.touchActive ? 'HUMAN TOUCH' : 'BACKGROUND (flutter)'}</div>
          <div data-testid="touch-duration" className="mt-1 text-sm text-[var(--muted)]">contact {(t.touchMs / 1000).toFixed(2)} s · score {t.score.toFixed(1)} / on {t.thresholds.on.toFixed(1)}</div>
          <div className="mt-2 text-sm">last: <b data-testid="touch-last-class" className="glow-spore">{t.lastClass || '—'}</b></div>
        </div>
        <div className="col-span-12 grid grid-cols-3 gap-3 lg:col-span-5">
          {CLASSES.map((c) => (
            <div key={c} className="border border-white/10 px-3 py-2"><div className="label-eyebrow">{c}</div><div data-testid={`touch-count-${c.toLowerCase()}`} className="font-display text-2xl font-bold">{t.counts[c]}</div></div>
          ))}
        </div>
        <div className="col-span-12 space-y-2 text-xs text-[var(--muted)] lg:col-span-3">
          <div data-testid="baseline-values">BASE CC {t.baseline.ccMean.toFixed(1)} ± {t.baseline.ccStd.toFixed(2)} · notes {t.baseline.rate.toFixed(2)}/s</div>
          <div>learned {t.baseline.learnedAt ? new Date(t.baseline.learnedAt).toLocaleTimeString() : 'default'}</div>
          <div className="label-eyebrow pt-2">Touch sensitivity <b data-testid="sensitivity-value" className="text-[var(--amber)]">{t.sensitivity}/5</b></div>
          <Slider data-testid="sensitivity-slider" min={1} max={5} step={1} value={[t.sensitivity]} onValueChange={(v) => spore.setSensitivity(v[0])} />
        </div>
      </div>
    </Panel>
  );
};

function replayRecording(rec, onDone, onTick) {
  const eng = createTouchEngine(spore.touch.getConfig(), { baseline: rec.baseline || spore.touch.getBaseline() });
  const out = [];
  eng.on((g) => ['TAP', 'STROKE', 'AMBIG', 'FLURRY', 'ALL_HANDS', 'STILL'].includes(g.type) && out.push(g));
  const t0 = performance.now();
  const end = rec.durationMs + 1500;
  let i = 0;
  const id = setInterval(() => {
    const el = performance.now() - t0;
    while (i < rec.events.length && rec.events[i].t <= el) eng.ingest(rec.events[i++]);
    eng.tick(el);
    onTick(el / end, out.length);
    if (el >= end) {
      clearInterval(id);
      const labelAt = (t) => [...rec.labels].reverse().find((l) => l.t <= t);
      const rows = out.map((g) => ({ ...g, label: (labelAt(g.t) || {}).label || '—' }));
      const scored = rows.filter((r) => r.label !== '—');
      const agree = scored.filter((r) => (LABEL_OK[r.label] || []).includes(r.type)).length;
      onDone({ rows, agree, scored: scored.length });
    }
  }, 20);
  return () => clearInterval(id);
}

export const RecorderPanel = () => {
  const [rec, setRec] = useState(null);
  const [label, setLabel] = useState(null);
  const [loaded, setLoaded] = useState(null);
  const [replay, setReplay] = useState(null);
  const data = useRef(null);

  useEffect(() => {
    if (!rec) return undefined;
    const unsub = spore.subscribe((e) => data.current.events.push({ type: e.type, channel: e.channel, note: e.note, velocity: e.velocity, cc: e.cc, value: e.value, role: e.role, t: Math.round(e.t - data.current.t0) }));
    const onKey = (e) => {
      const l = LABEL_KEYS[e.key.toLowerCase()];
      if (!l) return;
      data.current.labels.push({ label: l, t: Math.round(performance.now() - data.current.t0) });
      setLabel(l);
    };
    window.addEventListener('keydown', onKey);
    const id = setInterval(() => setRec((r) => r && { ...r, n: data.current.events.length }), 300);
    return () => {
      unsub();
      window.removeEventListener('keydown', onKey);
      clearInterval(id);
    };
  }, [rec && rec.started]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    data.current = { version: 1, startedAt: new Date().toISOString(), t0: performance.now(), events: [], labels: [{ label: 'HANDS_OFF', t: 0 }], baseline: spore.touch.getBaseline(), config: spore.touch.getConfig() };
    setLabel('HANDS_OFF');
    setRec({ started: Date.now(), n: 0 });
  };
  const stop = () => {
    const d = { ...data.current, durationMs: Math.round(performance.now() - data.current.t0) };
    delete d.t0;
    spore.uploadRecording('lab_recording', { events: d.events.map((e) => ({ ...e, t: e.t })), label: `labels:${d.labels.map((l) => `${l.label}@${l.t}`).join(',')}`, meta: { labels: d.labels, durationMs: d.durationMs, relativeT: true } });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([JSON.stringify(d)], { type: 'application/json' }));
    a.download = `spore-session-${d.startedAt.replace(/[:.]/g, '-')}.json`;
    a.click();
    setRec(null);
    setLoaded(d);
  };
  const load = async (file) => setLoaded(JSON.parse(await file.text()));
  const runReplay = () => {
    setReplay({ progress: 0, n: 0 });
    replayRecording(loaded, (res) => setReplay((r) => ({ ...r, progress: 1, result: res })), (p, n) => setReplay((r) => ({ ...r, progress: p, n })));
  };

  return (
    <Panel testId="recorder-panel" code="09 / SESSION RECORDER" title="Record · Label · Replay">
      <div className="flex flex-wrap items-center gap-3">
        {!rec ? <button data-testid="recorder-start-button" className="btn-lab primary" onClick={start}>Record</button> : <button data-testid="recorder-stop-button" className="btn-lab primary" onClick={stop}>Stop, save to server &amp; download</button>}
        <label className="btn-lab cursor-pointer">Load recording<input data-testid="recorder-load-input" type="file" accept="application/json" className="hidden" onChange={(e) => e.target.files[0] && load(e.target.files[0])} /></label>
        <button data-testid="recorder-replay-button" className="btn-lab" disabled={!loaded || (replay && replay.progress < 1)} onClick={runReplay}>Replay (real speed)</button>
        {rec && <span data-testid="recorder-status" className="text-sm glow-amber">● REC {rec.n} events · label <b>{label}</b> — keys: T tap · K stroke · 0 hands off · A all hands</span>}
        {loaded && !rec && <span data-testid="recorder-loaded" className="text-xs text-[var(--muted)]">loaded {loaded.events.length} events · {loaded.labels.length} labels · {(loaded.durationMs / 1000).toFixed(1)} s</span>}
      </div>
      {replay && (
        <div data-testid="replay-results" className="mt-4 text-sm">
          <div>replay {Math.round(replay.progress * 100)}% · {replay.n} classifications</div>
          {replay.result && (
            <>
              <div data-testid="replay-agreement" className="mt-2 font-display text-2xl font-bold glow-spore">AGREEMENT {replay.result.agree}/{replay.result.scored} {replay.result.scored ? `(${Math.round((replay.result.agree / replay.result.scored) * 100)}%)` : ''}</div>
              <div className="log-scroll mt-2 max-h-[180px] overflow-y-auto text-xs">
                {replay.result.rows.map((r, i) => <div key={i} className={(LABEL_OK[r.label] || []).includes(r.type) ? 'text-[var(--spore)]' : 'text-[var(--coral)]'}>{(r.t / 1000).toFixed(1)}s · {r.type} vs label {r.label}</div>)}
              </div>
            </>
          )}
        </div>
      )}
    </Panel>
  );
};

const SAMPLES = [
  '[excited] Bonsoir, mes amis! I am Professeur Henri Champignon, and zis is my laboratory!',
  'Go on, give it a gentle stroke... [whispers] see what happens.',
  '[hmm] Mmmmm... ah yes, zis must mean... it is good! [laughs] Magnifique!',
];

export const ProfessorVoicePanel = () => {
  const [text, setText] = useState(SAMPLES[0]);
  const [res, setRes] = useState(null);
  const play = async () => {
    const { unlockAudio, playEerie } = await import('@/spore/eerieAudio');
    const c = await unlockAudio();
    setRes({ busy: true });
    const t0 = performance.now();
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/professor/voice`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text }) });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const buf = await c.decodeAudioData(await r.arrayBuffer());
      playEerie(buf, { fx: false });
      setRes({ source: r.headers.get('X-Voice-Source'), ms: r.headers.get('X-Generation-Ms'), total: Math.round(performance.now() - t0) });
    } catch (e) {
      setRes({ error: e.message });
    }
  };
  return (
    <Panel testId="professor-voice-panel" code="10 / VOICE" title="Professeur Voice Test · ElevenLabs v3">
      <div className="mb-3 flex flex-wrap gap-2">{SAMPLES.map((s, i) => <button key={i} data-testid={`professor-sample-${i}`} className="btn-lab px-2 py-1 text-[0.65rem]" onClick={() => setText(s)}>Sample {i + 1}</button>)}</div>
      <textarea data-testid="professor-voice-text" className="select-lab h-20" value={text} onChange={(e) => setText(e.target.value)} />
      <div className="mt-3 flex items-center gap-4">
        <button data-testid="professor-voice-play" className="btn-lab primary" disabled={res && res.busy} onClick={play}>{res && res.busy ? 'Generating (~10s)…' : 'Speak'}</button>
        {res && !res.busy && <span data-testid="professor-voice-result" className="text-sm">{res.error ? `failed: ${res.error}` : `source ${res.source} · ${res.ms} ms server · ${res.total} ms total`}</span>}
      </div>
      <RepairScript />
    </Panel>
  );
};

const RepairScript = () => {
  const [st, setSt] = useState(null);
  const run = async () => {
    const { generateScript } = await import('@/show/professorPreloader');
    setSt({ status: 'generating', voices: 0, total: 0 });
    try {
      const doc = await generateScript(setSt, 'repair');
      setSt({ status: 'ready', doc });
    } catch (e) {
      setSt({ status: 'failed', error: e.message });
    }
  };
  const doc = st && st.doc;
  return (
    <div className="mt-4 border-t border-white/10 pt-3">
      <button data-testid="script-repair-button" className="btn-lab" disabled={st && st.status === 'generating'} onClick={run}>
        {st && st.status === 'generating' ? `Repairing ${st.voices}/${st.total}…` : 'Repair current script'}
      </button>
      <span className="ml-3 text-xs text-[var(--dim)]">re-does the 6 interpretations + failed voices only</span>
      {doc && <div data-testid="script-repair-result" className="mt-2 text-xs">{doc.script_id} · voices {doc.voice_coverage}/{doc.lines.length} · guesses {doc.lines.filter((l) => l.guess).map((l) => l.guess).join(' / ')}</div>}
      {st && st.status === 'failed' && <div data-testid="script-repair-error" className="mt-2 text-xs text-[var(--amber)]">repair failed: {st.error}</div>}
    </div>
  );
};
